import React, { useCallback, useEffect, useState } from 'react';

import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchLinkedAccounts, deleteLinkedAccount } from '@/store/slices/residents/homeSlice';
import { fetchInvitations, declineInvitation, sendInvitation } from '@/store/slices/residents/invitationsSlice';
import { LinkedAccount, Invitation } from '@/types/residents/home';
import { useToast } from '@/contexts/admin/ToastContext';
import { getDisplayName } from '@/utils/residents/utils';
import { LinkedAccountsContent } from '@/components/residents/redesign/linkedAccounts/LinkedAccountsContent';
import type { LinkedAccountsTab } from '@/components/residents/redesign/linkedAccounts/LinkedAccountsTabSegment';

import { NewInvitationModal } from '@/components/residents/linkedAccounts/mobile/NewInvitationModal';
import { EmailPopupModal } from '@/components/residents/linkedAccounts/mobile/EmailPopupModal';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';

export const ResidentLinkedAccountsScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const { linkedAccounts, isLoading } = useAppSelector((state) => state.home);
  const { showSuccess, showError } = useToast();

  // Tab state
  const [selectedTab, setSelectedTab] = useState<LinkedAccountsTab>('accounts');

  // Remove Connection Modal
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState<LinkedAccount | null>(null);

  // Email Popup Modal
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');

  // New Invitation Modal
  const [isNewInvitationModalVisible, setIsNewInvitationModalVisible] = useState(false);

  // Decline Invitation Modal
  const [isDeclineModalVisible, setIsDeclineModalVisible] = useState(false);
  const [invitationToDecline, setInvitationToDecline] = useState<Invitation | null>(null);

  // Cancel Invitation Modal
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState<Invitation | null>(null);

  useEffect(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  const handleTabChange = useCallback((tab: LinkedAccountsTab) => {
    setSelectedTab(tab);
  }, []);

  const handleRefresh = useCallback(() => {
    dispatch(fetchLinkedAccounts());
  }, [dispatch]);

  // ── Remove Connection ──────────────────────────────────────────────
  const handleRemovePress = useCallback((account: LinkedAccount) => {
    setAccountToRemove(account);
    setIsRemoveModalVisible(true);
  }, []);

  const handleRemoveCancel = useCallback(() => {
    setIsRemoveModalVisible(false);
    setAccountToRemove(null);
  }, []);

  const handleRemoveProceed = useCallback(async () => {
    if (!accountToRemove) return;
    setIsRemoveModalVisible(false);
    try {
      await dispatch(deleteLinkedAccount(accountToRemove.id)).unwrap();
      const displayName = getDisplayName(accountToRemove.user.firstName, accountToRemove.user.lastName) || accountToRemove.user.email;
      showSuccess(`You have successfully disconnected from ${displayName}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove linked account';
      showError(message);
    } finally {
      setAccountToRemove(null);
    }
  }, [accountToRemove, dispatch, showSuccess, showError]);

  const getRemoveConfirmMessage = (): string => {
    if (!accountToRemove) return '';
    const displayName = getDisplayName(accountToRemove.user.firstName, accountToRemove.user.lastName) || accountToRemove.user.email;
    return `Are you sure you want to remove connection to ${displayName}?`;
  };

  // ── Email Popup ────────────────────────────────────────────────────
  const handleEmailPress = useCallback((email: string) => {
    setSelectedEmail(email);
    setIsEmailModalVisible(true);
  }, []);

  const handleEmailModalClose = useCallback(() => {
    setIsEmailModalVisible(false);
    setSelectedEmail('');
  }, []);

  // ── New Invitation ─────────────────────────────────────────────────
  const handleSendInvite = useCallback(() => {
    setIsNewInvitationModalVisible(true);
  }, []);

  const handleNewInvitationClose = useCallback(() => {
    setIsNewInvitationModalVisible(false);
  }, []);

  const handleSendInvitation = useCallback(async (email: string, message: string) => {
    try {
      await dispatch(sendInvitation({ email, message })).unwrap();
      setIsNewInvitationModalVisible(false);
      showSuccess(`Invitation sent to ${email}`);
      dispatch(fetchInvitations());
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation';
      showError(errorMessage);
      throw error;
    }
  }, [dispatch, showSuccess, showError]);

  // ── Decline Invitation ─────────────────────────────────────────────
  const handleDeclineInvitation = useCallback((invitation: Invitation) => {
    setInvitationToDecline(invitation);
    setIsDeclineModalVisible(true);
  }, []);

  const handleDeclineCancel = useCallback(() => {
    setIsDeclineModalVisible(false);
    setInvitationToDecline(null);
  }, []);

  const handleDeclineProceed = useCallback(async () => {
    if (!invitationToDecline) return;
    setIsDeclineModalVisible(false);
    try {
      await dispatch(declineInvitation({ invitationId: invitationToDecline.id, sender: invitationToDecline.sender })).unwrap();
      const displayName = getDisplayName(invitationToDecline.user.firstName, invitationToDecline.user.lastName) || invitationToDecline.user.email;
      showSuccess(`You have successfully declined an invitation from ${displayName}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to decline invitation';
      showError(message);
    } finally {
      setInvitationToDecline(null);
    }
  }, [invitationToDecline, dispatch, showSuccess, showError]);

  const getDeclineConfirmMessage = (): string => {
    if (!invitationToDecline) return '';
    const displayName = getDisplayName(invitationToDecline.user.firstName, invitationToDecline.user.lastName) || invitationToDecline.user.email;
    return `Are you sure you want to decline an invitation from ${displayName}?`;
  };

  // ── Cancel Invitation ──────────────────────────────────────────────
  const handleCancelInvitation = useCallback((invitation: Invitation) => {
    setInvitationToCancel(invitation);
    setIsCancelModalVisible(true);
  }, []);

  const handleCancelConfirmCancel = useCallback(() => {
    setIsCancelModalVisible(false);
    setInvitationToCancel(null);
  }, []);

  const handleCancelProceed = useCallback(async () => {
    if (!invitationToCancel) return;
    setIsCancelModalVisible(false);
    try {
      await dispatch(declineInvitation({ invitationId: invitationToCancel.id, sender: invitationToCancel.sender })).unwrap();
      showSuccess(`Invitation cancelled for ${invitationToCancel.user.email}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cancel invitation';
      showError(message);
    } finally {
      setInvitationToCancel(null);
    }
  }, [invitationToCancel, dispatch, showSuccess, showError]);

  const getCancelConfirmMessage = (): string => {
    if (!invitationToCancel) return '';
    return `Are you sure you want to cancel an invitation for ${invitationToCancel.user.email}?`;
  };

  return (
    <>
      <LinkedAccountsContent
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        accounts={linkedAccounts}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onRemove={handleRemovePress}
        onEmailPress={handleEmailPress}
        onSendInvite={handleSendInvite}
        onDeclineInvitation={handleDeclineInvitation}
        onCancelInvitation={handleCancelInvitation}
        contentPaddingBottom={40 + insets.bottom}
      />

      <ConfirmationModal
        visible={isRemoveModalVisible}
        message={getRemoveConfirmMessage()}
        onCancel={handleRemoveCancel}
        onProceed={handleRemoveProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />

      <EmailPopupModal
        visible={isEmailModalVisible}
        email={selectedEmail}
        onClose={handleEmailModalClose}
      />

      <NewInvitationModal
        visible={isNewInvitationModalVisible}
        onClose={handleNewInvitationClose}
        onSend={handleSendInvitation}
      />

      <ConfirmationModal
        visible={isDeclineModalVisible}
        message={getDeclineConfirmMessage()}
        onCancel={handleDeclineCancel}
        onProceed={handleDeclineProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />

      <ConfirmationModal
        visible={isCancelModalVisible}
        message={getCancelConfirmMessage()}
        onCancel={handleCancelConfirmCancel}
        onProceed={handleCancelProceed}
        cancelText="No, cancel"
        proceedText="Proceed"
      />
    </>
  );
};
